// Single source of truth for the app's grantable pages — reused by the
// sidebar nav (AppLayout.tsx), the route guard (ProtectedRoute.tsx), and
// the access-checkbox UI in Settings.tsx's Team tab, so the three stay in
// sync automatically instead of maintaining three separate lists.
//
// Settings is here too (its own last group, `navHidden`: it opens from the
// gear in the sidebar footer, not the module rail). Granting it lets a
// staff login open Settings, but never its Team & Access, Accounting or
// Subscription tabs (Settings.tsx keeps those owner only).

export interface PageDef {
  key: string;
  label: string;
  path: string;
  /** Renders a small "Soon" pill and disables the link — for a planned page
   *  with no route yet. */
  badge?: 'soon';
  /** ONE level of nesting only (a child never has its own children). A parent
   *  with children is a nav container: it is NOT itself access-checked or
   *  routed — only its leaves are. `path` on a parent is a hint/landing only. */
  children?: PageDef[];
  /** Marks a leaf as belonging to the owner-only "Accountant" sub-area of the
   *  Accounting module — rendered as an indented mini-section, and its route
   *  is gated `ownerOnly` rather than by `allowedPages`. */
  accountantOnly?: boolean;
  /** Grantable and routed, but not drawn in the module rail (Settings). */
  navHidden?: boolean;
}

/** Staff right: see cost and margin figures on the Sales pages. */
export const COST_MARGIN_KEY = 'see-cost-margin';

export interface PageGroup {
  label: string;
  pages: PageDef[];
}

// 3-level model: group → page → (optional) child pages. See PHASES.md
// "Architecture > B. Navigation" and "> D. Proposed menu tree".
export const PAGE_GROUPS: PageGroup[] = [
  {
    label: 'Main',
    pages: [
      { key: 'dashboard', label: 'Dashboard', path: '/dashboard' },
      { key: 'demand-list', label: 'Demand List', path: '/demands' },
      { key: 'entity',    label: 'Entity',    path: '/entity' },
      { key: 'entity-history', label: 'Entity History', path: '/entity/history' },
      { key: 'reminders', label: 'Reminders', path: '/reminders' },
      { key: 'reports',   label: 'Reports',   path: '/reports' },
      { key: 'activity-log', label: 'Activity Log', path: '/activity' },
    ],
  },
  {
    label: 'Operations',
    pages: [
      {
        key: 'sales', label: 'Sales', path: '/sales',
        children: [
          { key: 'sales',             label: 'Sales',              path: '/sales' },
          { key: 'sales-returns',     label: 'Sales Returns / Credit Notes', path: '/sales/returns' },
          { key: 'sales-expenses',    label: 'Sales Expenses',              path: '/sales/expenses' },
          { key: 'memos',             label: 'Memo Out',                    path: '/memos' },
          { key: 'customer-advances', label: 'Customer Advances',           path: '/sales/advances' },
        ],
      },
      {
        key: 'inventory', label: 'Inventory', path: '/inventory',
        children: [
          { key: 'inventory',          label: 'Stock List',        path: '/inventory' },
          { key: 'inventory-merge',    label: 'Split & Merge',     path: '/inventory/merge' },
          { key: 'stock-transfer',     label: 'Stock Transfer',    path: '/inventory/transfer' },
          { key: 'stock-adjustment',   label: 'Stock Adjustment',  path: '/inventory/adjustment' },
          { key: 'stock-revaluation',  label: 'Stock Revaluation', path: '/inventory/revaluation' },
          { key: 'opening-stock',      label: 'Opening Stock',     path: '/inventory/opening' },
          { key: 'inventory-activity', label: 'Activity',          path: '/inventory/activity' },
        ],
      },
      {
        key: 'purchase', label: 'Purchase', path: '/purchase',
        children: [
          { key: 'purchase',           label: 'Purchase Invoices',  path: '/purchase' },
          { key: 'purchase-returns',   label: 'Purchase Returns',   path: '/purchase/returns' },
          { key: 'purchase-expenses',  label: 'Purchase Expenses',  path: '/purchase/expenses' },
          { key: 'memo-in',            label: 'Memo In',            path: '/purchase/memo-in' },
          { key: 'supplier-advances',  label: 'Supplier Advances',  path: '/purchase/advances' },
        ],
      }
    ],
  },
  {
    // Renamed from "Finance". AP / AR / Brokers / Bank / P&L are nested under
    // one collapsible "Accounting" parent; their keys and URLs are unchanged
    // so existing `allowedPages` arrays and bookmarks keep working. Later
    // phases split this into Receivables ▸ / Payables ▸ / Banking ▸ once each
    // has its own child pages (see PHASES.md § D).
    label: 'Accounting',
    pages: [
      {
        key: 'accounting', label: 'Accounting', path: '/profit',
        children: [
          { key: 'profit',         label: 'Profit & Loss',     path: '/profit' },
          { key: 'balance-sheet',  label: 'Balance Sheet',     path: '/accounting/balance-sheet' },
          { key: 'expenses',       label: 'Expenses',          path: '/accounting/expenses' },
          { key: 'income',         label: 'Income',            path: '/accounting/income' },
          { key: 'ar',             label: 'Receivable',        path: '/ar' },
          { key: 'ap',             label: 'Payable',           path: '/ap' },
          { key: 'brokers',        label: 'Brokers',           path: '/brokers' },
          { key: 'bank',           label: 'Bank',              path: '/bank' },
          { key: 'bank-reconcile', label: 'Bank Reconciliation', path: '/bank/reconcile' },
          { key: 'coa',            label: 'Chart of Accounts', path: '/accounting/coa',           accountantOnly: true },
          { key: 'journal',        label: 'Journal Entries',   path: '/accounting/journal',       accountantOnly: true },
          { key: 'general-ledger', label: 'General Ledger',    path: '/accounting/ledger',        accountantOnly: true },
          { key: 'trial-balance',  label: 'Trial Balance',     path: '/accounting/trial-balance', accountantOnly: true },
        ],
      },
    ],
  },
  {
    label: 'Settings',
    pages: [
      { key: 'settings', label: 'Settings', path: '/settings', navHidden: true },
    ],
  },
  {
    // Not pages: extra rights ticked in Edit Access like one. No path.
    label: 'Extra Access',
    pages: [
      { key: COST_MARGIN_KEY, label: 'See cost & margin in Sales', path: '', navHidden: true },
    ],
  },
];

// One-level flatten: a parent contributes its children; a leaf contributes
// itself. A nav-container parent (has `children`) is intentionally absent
// from ALL_PAGES — it is never granted, routed, or access-checked.
export const ALL_PAGES: PageDef[] = PAGE_GROUPS.flatMap(g =>
  g.pages.flatMap(p => (p.children ? p.children : [p]))
);
export const ALL_PAGE_KEYS: string[] = ALL_PAGES.map(p => p.key);

/** Leaves that are gated `ownerOnly` (Accountant sub-area), not by allowedPages. */
export const ACCOUNTANT_PAGE_KEYS: string[] = ALL_PAGES.filter(p => p.accountantOnly).map(p => p.key);

export function pagePath(key: string): string | undefined {
  return ALL_PAGES.find(p => p.key === key)?.path;
}

/**
 * Owner: always full access. Staff: only pages explicitly granted.
 *
 * `allowedPages === undefined` (the field was never written) is treated as
 * full access, not zero — it means this account predates the access-control
 * feature entirely. Without this, every staff login that existed before
 * `allowedPages` shipped would have silently lost access to every page the
 * moment this went live (a real regression, caught in production: existing
 * users could still sign in but landed on the "no pages assigned" screen).
 * A staff user is only actually restricted once something explicitly wrote
 * `allowedPages` for them — an empty array `[]` (a brand-new login nobody's
 * granted anything to yet, or every page deliberately revoked) is the one
 * case that means zero access.
 */
export function canAccessPage(
  user: { role: string; allowedPages?: string[] } | null | undefined,
  key: string
): boolean {
  if (!user) return false;
  if (user.role === 'owner') return true;
  if (user.allowedPages === undefined) return true;
  return user.allowedPages.includes(key);
}

/** First page (in registry order) a user can access, or null if none.
 *  Skips Accountant-only leaves so a staff redirect never lands there. */
export function firstAllowedPath(
  user: { role: string; allowedPages?: string[] } | null | undefined
): string | null {
  if (!user) return null;
  const key = ALL_PAGE_KEYS.find(
    k => !ACCOUNTANT_PAGE_KEYS.includes(k) && !!pagePath(k) && canAccessPage(user, k),
  );
  return key ? (pagePath(key) ?? null) : null;
}

type NavUser = { role: string; allowedPages?: string[] } | null | undefined;

/** True if a leaf should be shown/reachable for this user. Accountant-only
 *  leaves are owner-gated regardless of `allowedPages`. */
export function canAccessLeaf(user: NavUser, page: PageDef): boolean {
  if (page.accountantOnly) return user?.role === 'owner';
  return canAccessPage(user, page.key);
}

/**
 * `PAGE_GROUPS` pruned to what this user may see: a leaf shows iff
 * `canAccessLeaf`, a container parent shows iff ≥1 child is visible, a group
 * shows iff ≥1 page is visible. Used by the sidebar and by Settings'
 * PageAccessPicker (which additionally hides Accountant-only leaves — they're
 * not staff-grantable).
 */
export function visibleTree(user: NavUser): PageGroup[] {
  return PAGE_GROUPS
    .map(g => ({
      label: g.label,
      pages: g.pages
        .map((p): PageDef | null => {
          if (p.navHidden) return null;
          if (!p.children) return canAccessLeaf(user, p) ? p : null;
          const kids = p.children.filter(c => canAccessLeaf(user, c));
          return kids.length ? { ...p, children: kids } : null;
        })
        .filter((p): p is PageDef => p !== null),
    }))
    .filter(g => g.pages.length > 0);
}
